import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Radio, Edit2, Save, X, Phone, KeyRound, Eye, EyeOff, AlertCircle, CheckCircle, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import Toast from '../components/Toast';
import TwoFactorSetup from '../components/TwoFactorSetup';

const ROLE_LABEL = { admin: 'Administrateur', comite: 'Comité', membre: 'Membre' };
const ROLE_COLOR = { admin: { bg: '#FEF3C7', color: '#B45309' }, comite: { bg: '#EDE9FE', color: '#7C3AED' }, membre: { bg: '#EFF6FF', color: '#1D4ED8' } };

const inp = { width: '100%', border: '1.5px solid #E5E7EB', borderRadius: 8, padding: '9px 11px', fontSize: '0.875rem', outline: 'none', background: 'white', color: '#111827', boxSizing: 'border-box' };
const lbl = { fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9CA3AF', marginBottom: 3, display: 'block' };
const card = { background: 'white', borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' };

const Spinner = () => <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />;

const useWindowWidth = () => {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
};

const ProfilePage = () => {
  const { user, isAuthenticated, isAdmin, updateUser } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });
  const [formData, setFormData] = useState({ prenom: '', nom: '', indicatif: '', telephone: '' });
  const [show2FAModal, setShow2FAModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/auth'); return; }
    if (user) setFormData({ prenom: user.prenom, nom: user.nom, indicatif: user.indicatif, telephone: user.telephone || '' });
  }, [isAuthenticated, user, navigate]);

  const showToast = (msg, type = 'success') => setToast({ message: msg, type });

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const r = await authAPI.updateProfile(formData);
      updateUser(r.data.user); setEditing(false);
      showToast('Profil mis à jour avec succès.');
    } catch (err) { showToast(err.response?.data?.error || 'Erreur lors de la mise à jour', 'error'); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault(); setPwError(''); setPwSuccess(false);
    if (pwForm.next !== pwForm.confirm) return setPwError('Les nouveaux mots de passe ne correspondent pas.');
    if (pwForm.next.length < 8) return setPwError('Minimum 8 caractères requis.');
    setPwLoading(true);
    try {
      await authAPI.changePassword({ current_password: pwForm.current, new_password: pwForm.next });
      setPwSuccess(true); setPwForm({ current: '', next: '', confirm: '' });
      showToast('Mot de passe modifié avec succès.');
    } catch (err) { setPwError(err.response?.data?.error || 'Erreur lors du changement.'); }
    finally { setPwLoading(false); }
  };

  const isMobile = useWindowWidth() < 620;

  if (!user) return null;

  const roleStyle = ROLE_COLOR[user.role] || ROLE_COLOR.membre;
  const initials = `${user.prenom?.[0] || ''}${user.nom?.[0] || ''}`.toUpperCase();

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>

      <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#111827', marginBottom: 16 }}>Mon Profil</h2>

      {/* ── BANNIÈRE IDENTITÉ ── */}
      <div style={{ ...card, marginBottom: 16, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,#1D4ED8,#3B82F6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: 800, color: 'white', flexShrink: 0 }}>
          {initials}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#111827' }}>{user.prenom} {user.nom}</div>
          <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.875rem', color: '#3B82F6' }}>{user.indicatif}</div>
        </div>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: roleStyle.bg, color: roleStyle.color }}>
          {ROLE_LABEL[user.role] || user.role}
        </span>
      </div>

      {/* ── 2 COLONNES ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, alignItems: 'start' }}>

        {/* ── INFOS ── */}
        <div style={card}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827', display: 'flex', alignItems: 'center', gap: 7 }}>
              <Edit2 style={{ width: 14, height: 14, color: '#6B7280' }} /> Informations
            </div>
            {!editing && (
              <button onClick={() => setEditing(true)} style={{ padding: '4px 12px', borderRadius: 6, background: '#F3F4F6', border: '1px solid #E5E7EB', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>
                Modifier
              </button>
            )}
          </div>
          <div style={{ padding: '12px 16px' }}>
            {!editing ? (
              <div>
                {[
                  { icon: <Radio style={{ width: 13, height: 13 }} />, label: 'Indicatif', val: user.indicatif, mono: true },
                  { icon: <span style={{ fontSize: '0.85rem' }}>👤</span>, label: 'Prénom', val: user.prenom },
                  { icon: <span style={{ fontSize: '0.85rem' }}>👤</span>, label: 'Nom', val: user.nom },
                  { icon: <Phone style={{ width: 13, height: 13 }} />, label: 'Téléphone', val: user.telephone || <span style={{ color: '#D1D5DB', fontStyle: 'italic' }}>—</span> },
                  { icon: <Mail style={{ width: 13, height: 13 }} />, label: 'Email', val: user.email },
                ].map(({ icon, label, val, mono }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #F9FAFB' }}>
                    <span style={{ color: '#9CA3AF', flexShrink: 0 }}>{icon}</span>
                    <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', width: 82, flexShrink: 0 }}>{label}</span>
                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', fontFamily: mono ? 'monospace' : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</span>
                  </div>
                ))}
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  {[{ k: 'prenom', l: 'Prénom' }, { k: 'nom', l: 'Nom' }].map(({ k, l }) => (
                    <div key={k}><label style={lbl}>{l}</label>
                      <input required type="text" value={formData[k]} onChange={e => setFormData(f => ({ ...f, [k]: e.target.value }))} style={inp} />
                    </div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <div><label style={lbl}>Indicatif</label>
                    <input required type="text" value={formData.indicatif} onChange={e => setFormData(f => ({ ...f, indicatif: e.target.value.toUpperCase() }))} style={{ ...inp, fontFamily: 'monospace', fontWeight: 700 }} />
                  </div>
                  <div><label style={lbl}>Téléphone</label>
                    <input type="tel" placeholder="+41 79 ..." value={formData.telephone} onChange={e => setFormData(f => ({ ...f, telephone: e.target.value }))} style={inp} />
                  </div>
                </div>
                <div style={{ fontSize: '0.72rem', color: '#9CA3AF', marginBottom: 10, padding: '6px 8px', background: '#F9FAFB', borderRadius: 6 }}>
                  <Mail style={{ width: 11, height: 11, display: 'inline', marginRight: 4 }} />{user.email} <span style={{ opacity: 0.6 }}>(non modifiable)</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => { setEditing(false); setFormData({ prenom: user.prenom, nom: user.nom, indicatif: user.indicatif, telephone: user.telephone || '' }); }}
                    style={{ flex: 1, padding: '8px 0', borderRadius: 7, border: '1.5px solid #E5E7EB', background: 'white', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                    <X style={{ width: 13, height: 13 }} /> Annuler
                  </button>
                  <button type="submit" disabled={saving}
                    style={{ flex: 2, padding: '8px 0', borderRadius: 7, border: 'none', background: saving ? '#93C5FD' : '#1D4ED8', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                    {saving ? <Spinner /> : <><Save style={{ width: 13, height: 13 }} /> Enregistrer</>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* ── MOT DE PASSE ── */}
        <div style={card}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6' }}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827', display: 'flex', alignItems: 'center', gap: 7 }}>
              <KeyRound style={{ width: 14, height: 14, color: '#6B7280' }} /> Mot de passe
            </div>
          </div>
          <div style={{ padding: '12px 16px' }}>
            <form onSubmit={handleChangePassword}>
              <input type="text" name="email" autoComplete="username" value={user.email} readOnly style={{ display: 'none' }} />
              {pwError && <div style={{ display: 'flex', gap: 6, background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', padding: '8px 10px', borderRadius: 7, fontSize: '0.78rem', marginBottom: 10 }}><AlertCircle style={{ width: 13, height: 13, flexShrink: 0, marginTop: 1 }} />{pwError}</div>}
              {pwSuccess && <div style={{ display: 'flex', gap: 6, background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#15803D', padding: '8px 10px', borderRadius: 7, fontSize: '0.78rem', marginBottom: 10 }}><CheckCircle style={{ width: 13, height: 13, flexShrink: 0 }} /> Mot de passe modifié.</div>}
              {[{ k: 'current', l: 'Mot de passe actuel', a: 'current-password' }, { k: 'next', l: 'Nouveau mot de passe', a: 'new-password' }, { k: 'confirm', l: 'Confirmer', a: 'new-password' }].map(({ k, l, a }) => (
                <div key={k} style={{ marginBottom: 10 }}>
                  <label style={lbl}>{l}</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showPw[k] ? 'text' : 'password'} required value={pwForm[k]} onChange={e => setPwForm(f => ({ ...f, [k]: e.target.value }))} autoComplete={a} style={{ ...inp, paddingRight: 36 }} />
                    <button type="button" onClick={() => setShowPw(s => ({ ...s, [k]: !s[k] }))} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 0 }}>
                      {showPw[k] ? <EyeOff style={{ width: 14, height: 14 }} /> : <Eye style={{ width: 14, height: 14 }} />}
                    </button>
                  </div>
                </div>
              ))}
              <button type="submit" disabled={pwLoading} style={{ width: '100%', padding: '9px 0', borderRadius: 7, border: 'none', background: pwLoading ? '#FCD34D' : '#1D4ED8', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                {pwLoading ? <Spinner /> : <><KeyRound style={{ width: 13, height: 13 }} /> Modifier le mot de passe</>}
              </button>
              <button type="button" onClick={() => navigate('/reset-password')} style={{ width: '100%', marginTop: 8, padding: '7px 0', borderRadius: 7, border: '1px solid #E5E7EB', background: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 500, color: '#6B7280' }}>
                Mot de passe oublié ?
              </button>
            </form>
          </div>
        </div>

      </div>

      {/* ── AUTHENTIFICATION À DEUX FACTEURS (2FA) ── */}
      {isAdmin && (
        <div style={{...card, marginTop: 16}}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6' }}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827', display: 'flex', alignItems: 'center', gap: 7 }}>
              <Shield style={{ width: 14, height: 14, color: '#6B7280' }} /> Authentification à deux facteurs
            </div>
          </div>
          <div style={{ padding: '16px' }}>
            <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: 14, lineHeight: 1.5 }}>
              Ajoutez une couche de sécurité supplémentaire à votre compte administrateur en activant l'authentification à deux facteurs.
            </p>
            <button 
              onClick={() => setShow2FAModal(true)}
              style={{ 
                width: '100%', 
                padding: '10px 0', 
                borderRadius: 8, 
                border: 'none', 
                background: '#2563EB', 
                cursor: 'pointer', 
                fontSize: '0.875rem', 
                fontWeight: 600, 
                color: 'white', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: 6 
              }}>
              <Shield style={{ width: 14, height: 14 }} /> Gérer le 2FA
            </button>
          </div>
        </div>
      )}

      {show2FAModal && (
        <TwoFactorSetup 
          onClose={() => setShow2FAModal(false)}
          onSuccess={(msg) => {
            setShow2FAModal(false);
            showToast(msg);
          }}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default ProfilePage;
